import { createSessionSwrScope, type SwrKey } from '@/core/lib/swr/swr.key'
import {
  NO_RETRY_READ,
  type RequestPolicy,
} from '@/core/lib/fetcher/request-policy'

import { normalizeHabitIds } from '@/features/studio-renew/lib/character-habits'
import { normalizeOfficialCommands } from '@/features/studio-renew/official-commands'
import {
  internalReviewSchema,
  publicationSchema,
} from '@/features/studio-renew/schemas/publication.schema'

import type { Kind, StudioData } from '@/features/studio-renew/store'
import type {
  Chat,
  Character,
  Command,
  StatusBoard,
  StatusField,
  World,
} from '@/features/studio-renew/types'
import type { StudioCollectionResponse } from '@/features/studio-renew/schemas/studio-read.schema'

export type ServerMaterial = Record<string, unknown> & {
  id: string
  updatedAt?: string
}
export type ServerWork = {
  revision?: string
  internalReview?: unknown
  publication?: unknown
  audienceLocked?: boolean
  id: string
  work: Record<string, unknown>
  version: number
  published: boolean
  reviewStatus: string
  reviewNote?: string
  archived?: boolean
  updatedAt?: string
}

export const STUDIO_PATH: Record<Kind, string> = {
  chat: '/creator/me/work/',
  character: '/creator/me/character/',
  world: '/creator/me/world/',
  status: '/creator/me/status/',
  command: '/creator/me/command/',
}

export const STUDIO_READ_POLICY: RequestPolicy = Object.freeze({
  ...NO_RETRY_READ,
  id: 'studio-read-v1',
  maxResponseBytes: 4_194_304,
})

export function studioSwrKey(epoch: string | number, kind: Kind): SwrKey {
  return [
    STUDIO_PATH[kind],
    { scope: createSessionSwrScope(epoch), requestPolicy: STUDIO_READ_POLICY },
  ]
}

const timeOf = (iso: unknown) =>
  typeof iso === 'string' && Number.isFinite(Date.parse(iso))
    ? Date.parse(iso)
    : 0

type LegacyImageCondition = {
  situation?: string
  emotion?: string
  note?: string
}

/** 칩 · 메모로 저장한 옛 행도 상황 한 문장으로 읽는다 */
const conditionText = ({ situation, emotion, note }: LegacyImageCondition) =>
  [situation, emotion, note].filter(Boolean).join(' · ')

const shotsIn = (album: unknown) =>
  (
    album as (Record<string, unknown> & LegacyImageCondition)[] | undefined
  )?.map(({ emotion, note, situation, ...shot }) => ({
    ...shot,
    situation: conditionText({ situation, emotion, note }),
  }))

export function materialsIn<T>(
  kind: Kind,
  rows: ServerMaterial[],
  local: (kind: Kind, sid: string) => string,
  shape: (row: Record<string, unknown>) => Record<string, unknown> = (r) => r
) {
  return Object.fromEntries(
    rows.map((row) => {
      const lid = local(kind, row.id)
      // 서버 시각(createdAt · ISO updatedAt)은 화면 모양에 없다 — updatedAt 만 숫자로
      const rest: Record<string, unknown> = { ...row }
      delete rest.createdAt
      const material = shape(rest)
      return [
        lid,
        {
          ...material,
          ...(kind === 'character'
            ? {
                habits: normalizeHabitIds(lid, material.habits),
                album: shotsIn(material.album),
              }
            : {}),
          id: lid,
          updatedAt: timeOf(row.updatedAt),
        } as T,
      ]
    })
  )
}

/** 서버 작품 → 화면 작품. 서버는 공개 작품을 고치면 심사 상태를 draft 로 돌리고 공개는 따로 둔다 — 화면은 ‘공개 중’으로 보인다(backend.md 5-2) */
export function chatIn(
  w: ServerWork,
  local: (kind: Kind, sid: string) => string
): Chat {
  const {
    review: _storedReview,
    rejectReason,
    submittedAt,
    exploreCover,
    ...rest
  } = w.work as Omit<Partial<Chat>, 'submittedAt'> & {
    submittedAt?: string | null
    exploreCover?: string
  }
  const status =
    w.reviewStatus === 'submitted'
      ? 'pending'
      : w.reviewStatus === 'pending' ||
          w.reviewStatus === 'rejected' ||
          w.reviewStatus === 'approved'
        ? w.reviewStatus
        : 'draft'
  const characterIds = (rest.characterIds ?? []).map((x) =>
    local('character', x)
  )
  return {
    ...(rest as unknown as Chat),
    ...(!rest.introGif && exploreCover ? { introGif: exploreCover } : {}),
    id: local('chat', w.id),
    covers: rest.covers?.length ? rest.covers : rest.cover ? [rest.cover] : [],
    characterIds,
    lead: rest.lead ? local('character', rest.lead) : (characterIds[0] ?? null),
    worldId: rest.worldId ? local('world', rest.worldId) : null,
    statusId: rest.statusId ? local('status', rest.statusId) : null,
    commandIds: (rest.commandIds ?? []).map((x) => local('command', x)),
    // 기존 저장분의 빈 narration+image만 읽기 호환하고, 사진이 첨부된 실제 대사는 보존한다.
    // 이식된 행에 ID가 없으면 같은 저장본을 다시 읽어도 같은 키를 쓴다. 무작위 키는 가짜 충돌을 만든다.
    starts: (rest.starts ?? []).map((s, startIndex) => ({
      ...s,
      id: s.id || `import-start-${w.id}-${startIndex}`,
      intro: {
        ...s.intro,
        lines: s.intro.lines.map((raw, lineIndex) => {
          const l = raw.id
            ? raw
            : { ...raw, id: `import-line-${w.id}-${startIndex}-${lineIndex}` }
          return l.who === 'image' ||
            (l.who === 'narration' && !l.text && l.image)
            ? { ...l, who: 'image' as const, text: '' }
            : l.charId
              ? { ...l, charId: local('character', l.charId) }
              : l
        }),
      },
    })),
    // 장소 칸이 생기기 전 저장본은 시작 장면 · 컬렉션에 적어 둔 장소를 등록한 것으로 읽는다
    places: rest.places?.length
      ? rest.places
      : [
          ...new Set(
            [
              ...(rest.starts ?? []).map((s) => s.place),
              ...(rest.albumPlaces ?? []).map((p) => p.name),
            ].filter(Boolean)
          ),
        ],
    albumPlaces: (rest.albumPlaces ?? []).map((p) => ({
      ...p,
      scenes: p.scenes.map((s) => ({
        ...s,
        guide: s.guide && { situation: conditionText(s.guide) },
      })),
    })),
    officialCommands: normalizeOfficialCommands(rest.officialCommands ?? []),
    target:
      ({ all: '전체', female: '여성향', male: '남성향' } as const)[
        rest.target as unknown as 'all' | 'female' | 'male'
      ] ??
      rest.target ??
      '전체',
    review: w.published && status === 'draft' ? 'approved' : status,
    revision: w.revision,
    internalReview: internalReviewSchema.safeParse(w.internalReview).success
      ? internalReviewSchema.parse(w.internalReview)
      : undefined,
    publication: publicationSchema.safeParse(w.publication).success
      ? publicationSchema.parse(w.publication)
      : undefined,
    rejectReason:
      typeof rejectReason === 'string' && rejectReason
        ? rejectReason
        : undefined,
    submittedAt: submittedAt ? timeOf(submittedAt) : undefined,
    // 서버는 한 번 공개한 뒤(version > 0) 잠그고, 화면은 제출 순간 잠근다(5-2) — 둘 중 하나면 잠근다
    audienceLocked:
      w.audienceLocked ??
      (w.version > 0 ||
        (status === 'pending' &&
          !internalReviewSchema.safeParse(w.internalReview).success)),
    updatedAt: timeOf(w.updatedAt),
  }
}

type CollectionKey = 'worlds' | 'characters' | 'statuses' | 'commands' | 'chats'
type CollectionValue = StudioData[CollectionKey]

export function normalizeStudioCollection(
  kind: Kind,
  response: StudioCollectionResponse
): CollectionValue {
  const local = (_kind: Kind, id: string) => id
  if (kind === 'world')
    return materialsIn<World>(
      'world',
      (response as { worlds: ServerMaterial[] }).worlds,
      local,
      (row) => ({
        ...row,
        keywords: (row.keywords as World['keywords']).map((item, index) => ({
          ...item,
          id: item.id || `keyword-${row.id}-${index}`,
        })),
        places: (row.places as World['places']).map((item, index) => ({
          ...item,
          id: item.id || `place-${row.id}-${index}`,
        })),
      })
    )
  if (kind === 'character')
    return materialsIn<Character>(
      'character',
      (response as { characters: ServerMaterial[] }).characters,
      local,
      (row) => ({
        ...row,
        worldId:
          typeof row.worldId === 'string' ? local('world', row.worldId) : null,
      })
    )
  if (kind === 'status')
    return materialsIn<StatusBoard>(
      'status',
      (response as { statuses: ServerMaterial[] }).statuses,
      local,
      (row) => ({
        ...row,
        design: row.design ?? undefined,
        fields: ((row.fields as StatusField[] | undefined) ?? []).map(
          (field, index) => ({
            ...field,
            id: field.id || `status-field-${row.id}-${index}`,
            initial: String(field.initial ?? ''),
            options: field.options ?? [],
            min: field.min ?? undefined,
            max: field.max ?? undefined,
          })
        ),
      })
    )
  if (kind === 'command')
    return materialsIn<Command>(
      'command',
      (response as { commands: ServerMaterial[] }).commands,
      local
    )
  const works = (response as { works: ServerWork[] }).works.filter(
    (row) => !row.archived && Array.isArray(row.work?.characterIds)
  )
  return Object.fromEntries(works.map((row) => [row.id, chatIn(row, local)]))
}
